<script setup lang="ts">
const props = defineProps<{
  traceId: string
}>()

interface StoredFeedback {
  followUp: string
  rating: 0 | 1 | 2
  submissionId: string
  submittedFollowUp: boolean
}

const SURVEY_NAME = 'platform-agent'

const { t } = useI18n()
const { $posthog } = useNuxtApp()
const { survey } = usePostHogSurvey(SURVEY_NAME)

// One key per trace. Nothing outside this component writes it, so skip both the
// `storage` listener and the eager default write — an untouched answer should
// not add anything to a session store that already holds the whole transcript.
const feedback = useSessionStorage<StoredFeedback>(
  () => `docue-agent-feedback:${props.traceId}`,
  { followUp: '', rating: 0, submissionId: '', submittedFollowUp: false },
  { listenToStorageChanges: false, mergeDefaults: true, writeDefaults: false },
)
const isFollowUpOpen = ref(false)

const feedbackSurvey = computed(() => {
  // `api` surveys are the ones PostHog expects the app to render itself.
  if (survey.value?.type !== 'api')
    return undefined

  const ratingQuestionId = survey.value.questions.find(question => question.type === 'rating')?.id
  const followUpQuestionId = survey.value.questions.find(question => question.type === 'open')?.id

  if (!ratingQuestionId || !followUpQuestionId)
    return undefined

  return {
    id: survey.value.id,
    followUpQuestionId,
    ratingQuestionId,
  }
})

watch(feedbackSurvey, (currentSurvey) => {
  if (!currentSurvey || feedback.value.rating)
    return

  $posthog.capture('survey shown', {
    $survey_id: currentSurvey.id,
    $ai_trace_id: props.traceId,
  })
}, { immediate: true })

function captureResponse(
  surveyId: string,
  responses: Record<string, number | string>,
  completed: boolean,
) {
  if (!feedback.value.submissionId)
    feedback.value.submissionId = crypto.randomUUID()

  $posthog.capture('survey sent', {
    $survey_id: surveyId,
    ...Object.fromEntries(
      Object.entries(responses).map(([questionId, value]) => [
        `$survey_response_${questionId}`,
        value,
      ]),
    ),
    $ai_trace_id: props.traceId,
    $survey_submission_id: feedback.value.submissionId,
    $survey_completed: completed,
  })
}

function sendFeedback(rating: 1 | 2) {
  const currentSurvey = feedbackSurvey.value
  if (!currentSurvey)
    return

  const hasChanged = feedback.value.rating !== rating
  feedback.value.rating = rating
  isFollowUpOpen.value = true

  if (hasChanged) {
    captureResponse(
      currentSurvey.id,
      { [currentSurvey.ratingQuestionId]: rating },
      feedback.value.submittedFollowUp,
    )
  }
}

function sendFollowUp() {
  const currentSurvey = feedbackSurvey.value
  if (!currentSurvey || !feedback.value.rating)
    return

  const message = feedback.value.followUp.trim()
  feedback.value.followUp = message
  feedback.value.submittedFollowUp = true
  isFollowUpOpen.value = false

  captureResponse(
    currentSurvey.id,
    {
      [currentSurvey.ratingQuestionId]: feedback.value.rating,
      [currentSurvey.followUpQuestionId]: message,
    },
    true,
  )
}
</script>

<template>
  <div
    v-if="feedbackSurvey"
    class="flex flex-col items-start gap-2"
  >
    <div class="flex items-center gap-1.5">
      <p class="text-xs text-gray-400">
        {{ t('docueAgent.feedback.prompt') }}
      </p>
      <div class="flex items-center gap-0.5">
        <UiTooltip>
          <UiTooltipTrigger as-child>
            <UiButton
              type="button"
              size="2xs"
              color="neutral"
              variant="ghost"
              aspect-square
              :icon="feedback.rating === 1 ? 'docue:solid-thumbs-up' : 'docue:thumbs-up'"
              :class="{ 'bg-neutral-100 text-neutral-900': feedback.rating === 1 }"
              :aria-label="t('docueAgent.feedback.helpful')"
              :aria-pressed="feedback.rating === 1"
              @click="sendFeedback(1)"
            />
          </UiTooltipTrigger>
          <UiTooltipContent>
            {{ t('docueAgent.feedback.helpful') }}
          </UiTooltipContent>
        </UiTooltip>

        <UiTooltip>
          <UiTooltipTrigger as-child>
            <UiButton
              type="button"
              size="2xs"
              color="neutral"
              variant="ghost"
              aspect-square
              :icon="feedback.rating === 2 ? 'docue:solid-thumbs-down' : 'docue:thumbs-down'"
              :class="{ 'bg-neutral-100 text-neutral-900': feedback.rating === 2 }"
              :aria-label="t('docueAgent.feedback.notHelpful')"
              :aria-pressed="feedback.rating === 2"
              @click="sendFeedback(2)"
            />
          </UiTooltipTrigger>
          <UiTooltipContent>
            {{ t('docueAgent.feedback.notHelpful') }}
          </UiTooltipContent>
        </UiTooltip>
      </div>
    </div>

    <form
      v-if="isFollowUpOpen"
      class="flex w-72 flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-3 shadow-sm"
      @submit.prevent="sendFollowUp"
    >
      <label
        :for="`docue-agent-feedback-${traceId}`"
        class="text-xs font-medium text-neutral-700"
      >
        {{ t('docueAgent.feedback.message') }}
      </label>
      <UiTextarea
        :id="`docue-agent-feedback-${traceId}`"
        v-model="feedback.followUp"
        rows="3"
        :placeholder="t('docueAgent.feedback.placeholder')"
        class="min-h-20 resize-none text-sm"
      />
      <div class="flex justify-end">
        <UiButton
          type="submit"
          size="xs"
        >
          {{ t('submit') }}
        </UiButton>
      </div>
    </form>
  </div>
</template>
